import { createBrowserRouter, Navigate } from "react-router";
import Root from "./pages/Root";
import IndexPageRouter from "./pages/IndexPageRouter";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { EnterpriseProtectedRoute } from "./components/EnterpriseProtectedRoute";
import { EnterpriseAccessRoute } from "./components/EnterpriseAccessRoute";
import { RelationshipIntelligenceGate } from "./components/RelationshipIntelligenceGate";
import { RootErrorBoundary } from "./components/RootErrorBoundary";
import { adminChildrenRoutes } from "@admin-children-routes";

// ============================================================================
// DIRECT IMPORTS (No Lazy Loading) - Critical Path
// ============================================================================

// Authentication & Onboarding
import Auth from "./pages/Auth";
import EarlyAccessLanding from "./pages/EarlyAccessLanding";
import AuthCallback from "./pages/AuthCallback";
import EmailVerified from "./pages/EmailVerified";
import ModeSelection from "./pages/ModeSelection";
import Onboarding from "./pages/OnboardingCompressed";
import SinglesOnboarding from "./pages/SinglesOnboarding";

// Main App Pages
import Home from "./pages/Home";
import UserDashboard from "./pages/UserDashboard";
import Pricing from "./pages/Pricing";
import DailyCheckIn from "./pages/DailyCheckIn";
import QuickCheckIn from "./pages/QuickCheckIn";
import ActionCard from "./pages/ActionCard";
import Timeline from "./pages/Timeline";
import WeeklySummary from "./pages/WeeklySummary";
import Settings from "./pages/Settings";
import RelationshipManagement from "./pages/RelationshipManagement";
import Notifications from "./pages/Notifications";
import NotificationSettings from "./pages/NotificationSettings";
import Insights from "./pages/Insights";
import SituationalPacks from "./pages/SituationalPacks";
import PackDetail from "./pages/PackDetail";

// Singles Mode
import CompatibilityProfile from "./pages/CompatibilityProfile";
import SinglesPacks from "./pages/SinglesPacks";
import MatchCalculator from "./pages/MatchCalculator";
import DateJournal from "./pages/DateJournal";
import RelationshipTransition from "./pages/RelationshipTransition";
import EIAssessmentJourney from "./pages/EIAssessmentJourney.tsx";

// Community
import SocialComingSoon from "./pages/SocialComingSoon";
import RelationshipIntelligenceFAQ from "./pages/RelationshipIntelligenceFAQ";
import Referrals from "./pages/Referrals";
import BattleLobby from "./pages/BattleLobby";
import BattleRoom from "./pages/BattleRoom";

// Growth & Marketing
import WaitlistStatus from "./pages/WaitlistStatus";
import GrowthAnalytics from "./pages/GrowthAnalytics";
import AffiliatePartner from "./pages/AffiliatePartner";

// Legal & Info
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PhotoCredits from "./pages/PhotoCredits";
import About from "./pages/About";
import PasswordReset from "./pages/PasswordReset";
import AuthDiagnostic from "./pages/AuthDiagnostic";
import FAQ from "./pages/FAQ";
import Support from "./pages/Support";
import HelpSync from "./pages/HelpSync";
import Feedback from "./pages/Feedback";
import Contact from "./pages/Contact";

// Content
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import Resources from "./pages/Resources";
import Guides from "./pages/Guides";
import Press from "./pages/Press";
import FeatureMicroCoach from "./pages/FeatureMicroCoach";
import FeatureRelationshipIntelligence from "./pages/FeatureRelationshipIntelligence";
import BusinessInquiries from "./pages/BusinessInquiries";
import Enterprise from "./pages/Enterprise";
import EnterpriseStartHere from "./pages/EnterpriseStartHere";
import EnterpriseCheckIn from "./pages/EnterpriseCheckIn";
import EnterpriseWorkspaceHome from "./pages/EnterpriseWorkspaceHome";
import EnterpriseWorkspaceTimeline from "./pages/EnterpriseWorkspaceTimeline";
import EnterpriseWorkspaceInsights from "./pages/EnterpriseWorkspaceInsights";
import EnterpriseWorkspaceInterventions from "./pages/EnterpriseWorkspaceInterventions";
import EnterpriseWorkspaceObservability from "./pages/EnterpriseWorkspaceObservability";
import EnterpriseWorkspaceSettings from "./pages/EnterpriseWorkspaceSettings";
import EnterpriseSolutions from "./pages/EnterpriseSolutions";
import EnterpriseSecurity from "./pages/EnterpriseSecurity";
import EnterprisePilot from "./pages/EnterprisePilot";
import EnterprisePlatform from "./pages/EnterprisePlatform";
import EnterpriseExpansion from "./pages/EnterpriseExpansion";
import EnterpriseHowItWorks from "./pages/EnterpriseHowItWorks";
import EnterpriseIdentity from "./pages/EnterpriseIdentity";
import EnterpriseWhiteLabel from "./pages/EnterpriseWhiteLabel";
import EnterprisePartnerDocs from "./pages/EnterprisePartnerDocs";
import EnterpriseSalesDemo from "./pages/EnterpriseSalesDemo";
import SiteMap from "./pages/SiteMap";
import CompareTensionVsPaired from "./pages/CompareTensionVsPaired";
import PairedAlternatives from "./pages/PairedAlternatives";
import BestRelationshipApp from "./pages/BestRelationshipApp";
import BestCouplesApp from "./pages/BestCouplesApp";
import RelationshipCounselingApp from "./pages/RelationshipCounselingApp";
import AiCouplesTherapy from "./pages/AiCouplesTherapy";
import InvestorLanding from "./pages/InvestorLanding";

// Error
import NotFound from "./pages/NotFound";
import { isPhase2BattleEnabled } from "./lib/feature-flags";

// ============================================================================
// ROUTER CONFIGURATION
// ============================================================================

export const router = createBrowserRouter([
  {
    path: "/",
    Component: Root,
    errorElement: <RootErrorBoundary />,
    children: [
      { 
        index: true, 
        Component: IndexPageRouter
      },
      {
        path: "early-access",
        Component: EarlyAccessLanding,
      },
      {
        path: "beta-landing",
        element: <Navigate to="/" replace />,
      },
      {
        path: "launch",
        element: <Navigate to="/" replace />,
      },
      {
        path: "waitlist-blitz",
        element: <Navigate to="/" replace />,
      },
      { 
        path: "auth", 
        element: <Auth />
      },
      { 
        path: "auth/callback", 
        element: <AuthCallback />
      },
      { 
        path: "email-verified", 
        element: <EmailVerified />
      },
      { 
        path: "mode-selection", 
        element: <ProtectedRoute><ModeSelection /></ProtectedRoute>
      },
      { 
        path: "onboarding", 
        element: <ProtectedRoute><Onboarding /></ProtectedRoute>
      },
      { 
        path: "singles-onboarding", 
        element: <ProtectedRoute><SinglesOnboarding /></ProtectedRoute>
      },
      { 
        path: "compatibility-profile", 
        element: <ProtectedRoute><CompatibilityProfile /></ProtectedRoute>
      },
      { 
        path: "singles-packs", 
        element: <ProtectedRoute><SinglesPacks /></ProtectedRoute>
      },
      { 
        path: "match-calculator", 
        element: <ProtectedRoute><MatchCalculator /></ProtectedRoute>
      },
      { 
        path: "date-journal", 
        element: <ProtectedRoute><DateJournal /></ProtectedRoute>
      },
      { 
        path: "relationship-transition", 
        element: <ProtectedRoute><RelationshipTransition /></ProtectedRoute>
      },
      { 
        path: "home", 
        element: <ProtectedRoute><Home /></ProtectedRoute>
      },
      {
        path: "dashboard",
        element: <ProtectedRoute><UserDashboard /></ProtectedRoute>,
      },
      {
        path: "relationship-intelligence",
        element: <ProtectedRoute><Navigate to="/home#relationship-intelligence" replace /></ProtectedRoute>
      },
      {
        path: "relationship-intelligence/faq",
        element: <ProtectedRoute><RelationshipIntelligenceFAQ /></ProtectedRoute>
      },
      {
        path: "relationship-intelligence/assessment",
        element: <ProtectedRoute><RelationshipIntelligenceGate><EIAssessmentJourney /></RelationshipIntelligenceGate></ProtectedRoute>
      },
      {
        path: "signature-lab",
        element: <Navigate to="/home#relationship-intelligence" replace />
      },
      { 
        path: "check-in", 
        element: <ProtectedRoute><DailyCheckIn /></ProtectedRoute>
      },
      { 
        path: "quick-check-in", 
        element: <ProtectedRoute><QuickCheckIn /></ProtectedRoute>
      },
      { 
        path: "action", 
        element: <ProtectedRoute><ActionCard /></ProtectedRoute>
      },
      { 
        path: "timeline", 
        element: <ProtectedRoute><Timeline /></ProtectedRoute>
      },
      { 
        path: "summary", 
        element: <ProtectedRoute><WeeklySummary /></ProtectedRoute>
      },
      { 
        path: "settings", 
        element: <ProtectedRoute><Settings /></ProtectedRoute>
      },
      { 
        path: "relationships", 
        element: <ProtectedRoute><RelationshipManagement /></ProtectedRoute>
      },
      { 
        path: "pricing", 
        element: <Pricing />
      },
      { 
        path: "notifications", 
        element: <ProtectedRoute><Notifications /></ProtectedRoute>
      },
      { 
        path: "notification-settings", 
        element: <ProtectedRoute><NotificationSettings /></ProtectedRoute>
      },
      { 
        path: "insights", 
        element: <ProtectedRoute><Insights /></ProtectedRoute>
      },
      { 
        path: "packs", 
        element: <ProtectedRoute><SituationalPacks /></ProtectedRoute>
      },
      { 
        path: "pack/:packId", 
        element: <ProtectedRoute><PackDetail /></ProtectedRoute>
      },
      ...adminChildrenRoutes,
      { 
        path: "terms", 
        element: <Terms />
      },
      { 
        path: "privacy", 
        element: <Privacy />
      },
      {
        path: "photo-credits",
        element: <PhotoCredits />
      },
      { 
        path: "about", 
        element: <About />
      },
      { 
        path: "password-reset", 
        element: <PasswordReset />
      },
      { 
        path: "auth-diagnostic", 
        element: <AuthDiagnostic />
      },
      { 
        path: "faq", 
        element: <FAQ />
      },
      { 
        path: "support", 
        element: <Support />
      },
      {
        path: "help/sync",
        element: <HelpSync />,
      },
      { 
        path: "feedback", 
        element: <Feedback />
      },
      { 
        path: "beta-access", 
        element: <Navigate to="/" replace />
      },
      {
        path: "contact",
        element: <Contact />
      },
      { 
        path: "user-profile", 
        element: <Navigate to="/social-coming-soon" replace />
      },
      { 
        path: "community", 
        element: <Navigate to="/social-coming-soon" replace />
      },
      {
        path: "community/legacy",
        element: <Navigate to="/social-coming-soon" replace />,
      },
      {
        path: "battle",
        element: isPhase2BattleEnabled()
          ? <ProtectedRoute><BattleLobby /></ProtectedRoute>
          : <Navigate to="/home" replace />,
      },
      {
        path: "battle/:roomId",
        element: isPhase2BattleEnabled()
          ? <ProtectedRoute><BattleRoom /></ProtectedRoute>
          : <Navigate to="/home" replace />,
      },
      {
        path: "community/posts/:postId",
        element: <Navigate to="/social-coming-soon" replace />
      },
      {
        path: "social-coming-soon",
        element: <ProtectedRoute><SocialComingSoon /></ProtectedRoute>,
      },
      { 
        path: "tips-library", 
        element: <Navigate to="/community" replace />
      },
      {
        path: "tips-library/legacy",
        element: <Navigate to="/community/legacy?view=tips" replace />,
      },
      { 
        path: "referrals", 
        element: <ProtectedRoute><Referrals /></ProtectedRoute>
      },
      { 
        path: "waitlist-status", 
        element: <WaitlistStatus />
      },
      { 
        path: "growth-analytics", 
        element: <GrowthAnalytics />
      },
      { 
        path: "affiliate-partner", 
        element: <AffiliatePartner />
      },
      { 
        path: "blog", 
        element: <Blog />
      },
      { 
        path: "blog/:slug", 
        element: <BlogArticle />
      },
      { 
        path: "resources", 
        element: <Resources />
      },
      {
        path: "guides",
        element: <Guides />
      },
      {
        path: "press",
        element: <Press />
      },
      {
        path: "features/conversation-micro-coach",
        element: <FeatureMicroCoach />
      },
      {
        path: "features/relationship-intelligence",
        element: <FeatureRelationshipIntelligence />
      },
      {
        path: "business-inquiries",
        element: <BusinessInquiries />
      },
      {
        path: "enterprise",
        element: (
          <EnterpriseAccessRoute>
            <Enterprise />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/start-here",
        element: <EnterpriseStartHere />
      },
      {
        path: "enterprise/workspace",
        element: <Navigate to="/work/home" replace />
      },
      {
        path: "work",
        element: <Navigate to="/work/home" replace />,
      },
      {
        path: "work/home",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceHome />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "work/timeline",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceTimeline />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "work/insights",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceInsights />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "work/interventions",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceInterventions />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "work/observability",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceObservability />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "work/settings",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceSettings />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "work/check-in",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseCheckIn />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/home",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceHome />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/timeline",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceTimeline />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/insights",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceInsights />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/interventions",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceInterventions />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/observability",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceObservability />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/settings",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWorkspaceSettings />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/workspace/check-in",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseCheckIn />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/solutions",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseSolutions />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/security",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseSecurity />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/pilot",
        element: (
          <EnterpriseAccessRoute>
            <EnterprisePilot />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/platform",
        element: (
          <EnterpriseAccessRoute>
            <EnterprisePlatform />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/how-it-works",
        element: <EnterpriseHowItWorks />
      },
      {
        path: "enterprise/identity",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseIdentity />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/whitelabel",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseProtectedRoute>
              <EnterpriseWhiteLabel />
            </EnterpriseProtectedRoute>
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/partner-docs",
        element: (
          <EnterpriseAccessRoute>
            <EnterprisePartnerDocs />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/expansion",
        element: (
          <EnterpriseAccessRoute>
            <EnterpriseExpansion />
          </EnterpriseAccessRoute>
        )
      },
      {
        path: "enterprise/client-site",
        element: <Navigate to="/enterprise" replace />
      },
      {
        path: "enterprise/client-site/preview",
        element: <Navigate to="/enterprise" replace />
      },
      {
        path: "enterprise/sales-demo",
        element: <EnterpriseSalesDemo />
      },
      {
        path: "enterprise/live-demo",
        element: <EnterpriseSalesDemo />
      },
      {
        path: "site-map",
        element: <SiteMap />
      },
      {
        path: "compare/tension-vs-paired",
        element: <CompareTensionVsPaired />
      },
      {
        path: "alternatives/paired",
        element: <PairedAlternatives />
      },
      {
        path: "best-relationship-app",
        element: <BestRelationshipApp />
      },
      {
        path: "best-couples-app",
        element: <BestCouplesApp />
      },
      {
        path: "relationship-counseling-app",
        element: <RelationshipCounselingApp />
      },
      {
        path: "ai-couples-therapy",
        element: <AiCouplesTherapy />
      },
      {
        path: "investors",
        element: <InvestorLanding />
      },
      { 
        path: "*", 
        element: <NotFound />
      },
    ],
  },
]);